import { useState, useEffect } from 'react'
import { AxiosError } from 'axios'
import { useLoaderData, useNavigate } from 'react-router'
import { createClient } from '@/api-client'
import { Subscriptions } from '@/api-client/Subscriptions'
import { Panel } from '@/components/subscription/panel'
import { BranchPageHeading } from '@/components/subscription/branch-page-heading'

export const loader = async () => {
  const subscription = await fetchSubscription()
  const address = await fetchAddress(subscription).catch(() => ({ data: { data: { attributes: {} } } }) as Awaited<ReturnType<typeof fetchAddress>>)

  return [subscription, address] as const
}

async function fetchSubscription() {
  const subscriptions = await createClient(Subscriptions).getSubscriptions({ category: 'green' })

  return subscriptions.data.data[0]
}

function fetchAddress(subscription) {
  return createClient(Subscriptions).getSubscriptionsSubscriptionIdAddress(subscription.id)
}

function updateSubscriptionAddress(subscription, address) {
  const params = {
    kind: address.with_sender_name ? 'gift' : 'home',
    sender_name: address.with_sender_name ? address.sender_name : '',
    postal_code: address.postal_code,
    state: address.state,
    city_and_street: address.city_and_street,
    others: address.others,
    name: address.name,
    phone_number: address.phone_number,
  }

  return createClient(Subscriptions).putSubscriptionsSubscriptionIdAddress(subscription.id, { ...params })
}

export const SubscriptionBranchMypageAddress: React.FC = () => {
  const [
    subscription,
    {
      data: { data: registeredAddress },
    },
  ] = useLoaderData<typeof loader>()

  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [streetError, setStreetError] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [address, setAddress] = useState(() => {
    if (registeredAddress) {
      return {
        ...registeredAddress.attributes,
        with_sender_name: !!registeredAddress.attributes.sender_name,
      }
    } else {
      return {
        name: '',
        postal_code: '',
        state: '',
        city_and_street: '',
        others: '',
        phone_number: '',
        with_sender_name: false,
        sender_name: '',
      }
    }
  })

  const inputClass = 'w-full border border-gray rounded-lg px-3 py-3 text-[14px] outline-none focus:border-black transition-colors bg-white'

  useEffect(() => {
    const { postal_code } = address

    if (postal_code.length < 7) return

    if (!address.city_and_street) {
      const code = postal_code.replace('-', '')
      window
        .fetch(`https://joe-noh.github.io/jp-postal-code-api/api/v1/${code}.json`)
        .then((res) => res.json())
        .then((json) => {
          const addr = json['addresses'][0]['ja']

          if (addr) {
            setAddress((prev) => ({
              ...prev,
              state: addr['prefecture'],
              city_and_street: addr['address1'] + addr['address2'],
            }))
          }
        })
        .catch(() => {}) // 補完に失敗しても無視
    }
  }, [address.postal_code])

  const handleChangeAddress = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    setAddress((prev) => ({ ...prev, [name]: value }))
  }

  const validateCityAndStreet = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.target.value.match(/\d/)) {
      setStreetError('番地が記載されていない可能性があります。ご確認ください')
    } else {
      setStreetError('')
    }
  }

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault()

    if (!isSubmitting) {
      setIsSubmitting(true)

      try {
        await updateSubscriptionAddress(subscription, address)

        navigate('/subscription/branch/mypage', { state: { message: 'お届け先を変更しました' } })
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response.data.error.message_displayable || '入力内容をご確認ください')
        }
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <form className="w-full space-y-8 pt-4 pb-18" onSubmit={handleSubmit}>
      <BranchPageHeading main="お届け先の変更" />

      <div className="space-y-6">
        <Panel padding="m">
          <div className="w-full space-y-5">
            <div>
              <label htmlFor="name" className="block text-[12px] text-dark-gray mb-2">
                お名前 <span className="text-accent">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="山田 花子"
                required
                value={address.name}
                className={inputClass}
                onChange={handleChangeAddress}
              />
            </div>
            <div>
              <label htmlFor="postal-code" className="block text-[12px] text-dark-gray mb-2">
                郵便番号 <span className="text-accent">*</span>
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  id="postal-code"
                  name="postal_code"
                  placeholder="0000000"
                  required
                  value={address.postal_code}
                  onChange={handleChangeAddress}
                  className="w-40 border border-gray rounded-lg px-3 py-3 text-[14px] outline-none focus:border-black transition-colors bg-white"
                />
              </div>
            </div>
            <div>
              <label htmlFor="state" className="block text-[12px] text-dark-gray mb-2">
                都道府県 <span className="text-accent">*</span>
              </label>
              <select id="state" name="state" required value={address.state} className={inputClass} onChange={handleChangeAddress}>
                <option value="">選択してください</option>
                <option value="北海道">北海道</option>
                <option value="青森県">青森県</option>
                <option value="岩手県">岩手県</option>
                <option value="宮城県">宮城県</option>
                <option value="秋田県">秋田県</option>
                <option value="山形県">山形県</option>
                <option value="福島県">福島県</option>
                <option value="茨城県">茨城県</option>
                <option value="栃木県">栃木県</option>
                <option value="群馬県">群馬県</option>
                <option value="埼玉県">埼玉県</option>
                <option value="千葉県">千葉県</option>
                <option value="東京都">東京都</option>
                <option value="神奈川県">神奈川県</option>
                <option value="新潟県">新潟県</option>
                <option value="富山県">富山県</option>
                <option value="石川県">石川県</option>
                <option value="福井県">福井県</option>
                <option value="山梨県">山梨県</option>
                <option value="長野県">長野県</option>
                <option value="岐阜県">岐阜県</option>
                <option value="静岡県">静岡県</option>
                <option value="愛知県">愛知県</option>
                <option value="三重県">三重県</option>
                <option value="滋賀県">滋賀県</option>
                <option value="京都府">京都府</option>
                <option value="大阪府">大阪府</option>
                <option value="兵庫県">兵庫県</option>
                <option value="奈良県">奈良県</option>
                <option value="和歌山県">和歌山県</option>
                <option value="鳥取県">鳥取県</option>
                <option value="島根県">島根県</option>
                <option value="岡山県">岡山県</option>
                <option value="広島県">広島県</option>
                <option value="山口県">山口県</option>
                <option value="徳島県">徳島県</option>
                <option value="香川県">香川県</option>
                <option value="愛媛県">愛媛県</option>
                <option value="高知県">高知県</option>
                <option value="福岡県">福岡県</option>
                <option value="佐賀県">佐賀県</option>
                <option value="長崎県">長崎県</option>
                <option value="熊本県">熊本県</option>
                <option value="大分県">大分県</option>
                <option value="宮崎県">宮崎県</option>
                <option value="鹿児島県">鹿児島県</option>
                <option value="沖縄県" disabled>
                  沖縄県
                </option>
              </select>
            </div>
            <div>
              <label htmlFor="city-and-street" className="block text-[12px] text-dark-gray mb-2">
                市区町村・番地 <span className="text-accent">*</span>
              </label>
              <input
                type="text"
                id="city-and-street"
                name="city_and_street"
                placeholder="渋谷区1-2-3"
                required
                value={address.city_and_street}
                className={inputClass}
                onChange={handleChangeAddress}
                onBlur={validateCityAndStreet}
              />
              {streetError && <p className="text-[12px] text-accent mt-1">{streetError}</p>}
            </div>
            <div>
              <label htmlFor="others" className="block text-[12px] text-dark-gray mb-2">
                建物名・部屋番号
              </label>
              <input
                type="text"
                placeholder="○○マンション 101号室"
                id="others"
                name="others"
                value={address.others}
                className={inputClass}
                onChange={handleChangeAddress}
              />
            </div>
            <div>
              <label htmlFor="phone-number" className="block text-[12px] text-dark-gray mb-2">
                電話番号 <span className="text-accent">*</span>
              </label>
              <input
                type="text"
                id="phone-number"
                name="phone_number"
                placeholder="09012345678"
                maxLength={11}
                required
                value={address.phone_number}
                className={inputClass}
                onChange={handleChangeAddress}
              />
            </div>
            <div>
              <label className="flex items-center gap-3 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  name="with_sender_name"
                  className="w-4 h-4 rounded accent-black"
                  checked={address.with_sender_name}
                  onChange={(e) => setAddress((prev) => ({ ...prev, with_sender_name: e.target.checked }))}
                />
                <span className="text-[14px] text-main">注文者と受取人が異なる</span>
              </label>
            </div>

            {address.with_sender_name && (
              <div className="space-y-4 pt-4 border-t border-gray/40">
                <div className="bg-bg rounded-xl p-4 space-y-2">
                  <p className="text-[12px] font-medium text-main">ご本人さま以外への配送について</p>
                  <ul className="space-y-1">
                    <li className="text-[12px] text-dark-gray leading-relaxed">・明細書など金額のわかるものは同封されません</li>
                    <li className="text-[12px] text-dark-gray leading-relaxed">・あらかじめ受取人の方に荷物が届くことをお伝えください</li>
                  </ul>
                </div>
                <div>
                  <label htmlFor="sender-name" className="block text-[12px] text-dark-gray mb-2">
                    送り主（ご注文者）のお名前 <span className="text-accent">*</span>
                  </label>
                  <input
                    type="text"
                    id="sender-name"
                    name="sender_name"
                    placeholder="田中 みどり"
                    className={inputClass}
                    required={address.with_sender_name}
                    value={address.sender_name}
                    onChange={handleChangeAddress}
                  />
                  <p className="text-[11px] text-dark-gray mt-2 leading-relaxed">送り主のお名前があることで、相手が安心してお花を受け取ることができます</p>
                </div>
              </div>
            )}
          </div>
        </Panel>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-5 bg-black text-white text-[15px] font-medium rounded-full cursor-pointer hover:bg-main disabled:bg-main transition-colors"
        >
          保存する
        </button>
        {errorMessage && <p className="text-[13px] text-accent mt-1">{errorMessage}</p>}
      </div>
    </form>
  )
}
